/**
 * byrna-cart-restrictions.js — #1364
 * Cart-side consumer of `byrna:stateDetected`. Personalizes the cart drawer:
 *  - per-item restriction lines flip to ✓ Ships / ⚠ Can't ship
 *  - the footer CTA switches between dropdown / verify (IP) / confirmed views
 *  - a personalized summary replaces the generic one
 * Scoped to the cart container (.theme-ajax-cart) so the shared state-capture-cta
 * snippet on PDPs/flyout is never affected. Re-hydrates after AJAX cart re-renders
 * (add/remove item swaps innerHTML) via a MutationObserver. No storage, no network.
 */
(function () {
  'use strict';

  var OBS_OPTS = { childList: true, subtree: true };
  var carts = [];          // { node, obs } for each watched cart root
  var lastViewKey = null;  // dedupes the GA4 state_result impression

  function esc(s) {
    var d = document.createElement('div');
    d.textContent = (s == null) ? '' : s;
    return d.innerHTML;
  }

  function show(el, on) {
    if (el) el.classList.toggle('is-hidden', !on);
  }

  // Draw the eye to the state selector when "Confirm your state" is clicked —
  // programmatic focus alone is invisible on desktop. Scroll it into view and
  // run a brief attention pulse (CSS honors prefers-reduced-motion). (#1364)
  function pulseAttention(el) {
    if (!el) return;
    try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
    el.classList.remove('is-attention');
    void el.offsetWidth; // force reflow so re-adding restarts the animation
    el.classList.add('is-attention');
    window.setTimeout(function () { el.classList.remove('is-attention'); }, 1400);
  }

  // Canonicalize a state name for MATCHING only (display uses the original).
  // Guards against non-canonical metafield spellings — DC has 3 known variants.
  function canon(s) {
    s = (s == null ? '' : String(s)).trim().toLowerCase();
    if (s === 'washington dc' || s === 'washington d.c.' ||
        s === 'district of columbia (d.c.)' || s === 'd.c.' || s === 'dc') {
      return 'district of columbia';
    }
    return s;
  }

  // Per-item flip (cart items carry [data-cart-restrict]). Also flags the whole
  // line item (.ajax-cart__product) with .is-cant-ship so CSS can tint it; class
  // is set here (not via CSS :has) for older-browser safety. (#1364)
  function flipItems(state) {
    document.querySelectorAll('[data-cart-restrict]').forEach(function (el) {
      var def = el.getAttribute('data-restrict-default') || el.textContent;
      var item = el.closest('.ajax-cart__product');
      if (!state) {
        el.textContent = def;
        el.classList.remove('is-ok', 'is-warn');
        if (item) item.classList.remove('is-cant-ship');
        return;
      }
      var list = (el.getAttribute('data-cart-restrict') || '').split(',').map(function (s) {
        return canon(s);
      });
      if (list.indexOf(canon(state)) > -1) {
        el.textContent = "⚠ Can't ship to " + state;
        el.classList.add('is-warn');
        el.classList.remove('is-ok');
        if (item) item.classList.add('is-cant-ship');
      } else {
        el.textContent = "✓ Ships to " + state;
        el.classList.add('is-ok');
        el.classList.remove('is-warn');
        if (item) item.classList.remove('is-cant-ship');
      }
    });
  }

  function countRestricted(root, state) {
    var count = 0, name = null;
    root.querySelectorAll('[data-cart-restrict]').forEach(function (el) {
      var list = (el.getAttribute('data-cart-restrict') || '').split(',').map(function (s) {
        return canon(s);
      });
      if (state && list.indexOf(canon(state)) > -1) {
        count++;
        name = el.getAttribute('data-item-name');
      }
    });
    return { count: count, name: name };
  }

  function renderFooter(state, source) {
    var roots = document.querySelectorAll('.theme-ajax-cart');
    if (!roots.length) return;
    var confident = (source === 'quiz' || source === 'manual');
    var shown = false;
    var outcome = null;

    roots.forEach(function (root) {
      var cta     = root.querySelector('[data-state-cta]');
      var verify  = root.querySelector('[data-state-verify]');
      var result  = root.querySelector('[data-state-result]');
      var summary = root.querySelector('[data-cart-restrict-summary]');
      var intro   = cta && cta.querySelector('.byrna-state-cta__intro');
      if (!cta || !result) return;

      if (!state) {                       // DEFAULT — dropdown only
        show(cta, true); show(verify, false); show(result, false); show(summary, true); show(intro, true);
        return;
      }
      if (!confident) {                   // VERIFY — IP/cached guess: the verify note
        // does the talking; "select your state" would contradict the pre-filled dropdown
        show(cta, true); show(verify, true); show(result, false); show(summary, false); show(intro, false);
        return;
      }
      show(intro, true); // restored for when Change re-expands the dropdown
      // CONFIRMED — quiz/manual: collapse to result row.
      // Preserve keyboard focus if it was inside the dropdown we're hiding.
      var hadFocus = cta.contains(document.activeElement);
      show(cta, false); show(verify, false); show(result, true); show(summary, false);

      var info = countRestricted(root, state);
      var icon = result.querySelector('[data-result-icon]');
      var text = result.querySelector('[data-result-text]');
      result.classList.remove('is-ok', 'is-warn');

      if (info.count === 0) {
        result.classList.add('is-ok');
        if (icon) icon.textContent = '✓';
        if (text) text.innerHTML = 'Everything ships to <b>' + esc(state) + '</b>.';
        outcome = 'eligible';
      } else {
        result.classList.add('is-warn');
        if (icon) icon.textContent = '⚠';
        var who = (info.count === 1 && info.name) ? esc(info.name) : (info.count + ' items');
        if (text) {
          text.innerHTML = '<b>' + who + "</b> can't ship to <b>" + esc(state) + '</b>.';
        }
        outcome = 'restricted';
      }

      if (hadFocus) {
        var cb = result.querySelector('[data-state-change]');
        if (cb) cb.focus();
      }
      shown = true;
    });

    // GA4 impression — once per distinct (state, outcome); reset on non-confirmed.
    if (shown && outcome) {
      var key = state + '|' + outcome;
      if (key !== lastViewKey) {
        lastViewKey = key;
        if (window.ByrnaGA4) {
          window.ByrnaGA4.featureView('cart_pre_checkout_messaging', '1364', {
            surface: 'cart', element: 'state_result', step: state, label: outcome
          });
        }
      }
    } else {
      lastViewKey = null;
    }
  }

  function bootstrap(state, source) {
    flipItems(state);
    renderFooter(state, source);
  }

  // Reserve exactly enough scroll space for the absolutely-positioned footer, whose height
  // changes with cart state (selector vs result row, 1- vs 2-line messages). Set on
  // documentElement — outside the cart subtree — so it never retriggers the cart observer. (#1364)
  function syncFooterReserve() {
    var max = 0;
    document.querySelectorAll('.is-drawer .ajax-cart__details-wrapper').forEach(function (f) {
      if (f.offsetHeight > max) max = f.offsetHeight;
    });
    if (max) {
      document.documentElement.style.setProperty('--cart-footer-reserve', (max + 16) + 'px');
    }
  }

  function rehydrate() {
    if (window.byrnaDetectedState) {
      bootstrap(window.byrnaDetectedState.state, window.byrnaDetectedState.source);
    } else {
      bootstrap(null, null);
    }
    syncFooterReserve();
  }

  // Re-personalize after AJAX cart re-renders. All observers are disconnected
  // around our synchronous writes so our own DOM edits never retrigger them.
  function onCartMutation() {
    carts.forEach(function (c) { c.obs.disconnect(); });
    rehydrate();
    carts.forEach(function (c) { c.obs.observe(c.node, OBS_OPTS); });
  }

  function watchCarts() {
    document.querySelectorAll('.theme-ajax-cart').forEach(function (node) {
      var obs = new MutationObserver(onCartMutation);
      carts.push({ node: node, obs: obs });
      obs.observe(node, OBS_OPTS);
    });
  }

  rehydrate();
  watchCarts();

  // Keep the footer reserve correct on resize / orientation change (debounced).
  var reserveTimer;
  window.addEventListener('resize', function () {
    window.clearTimeout(reserveTimer);
    reserveTimer = window.setTimeout(syncFooterReserve, 150);
  });

  document.addEventListener('byrna:stateDetected', function (e) {
    bootstrap(e.detail.state, e.detail.source);
  });

  document.addEventListener('click', function (e) {
    var changeBtn = e.target.closest('[data-state-change]');
    if (changeBtn) {
      var root = changeBtn.closest('.theme-ajax-cart');
      if (!root) return; // flyout/PDP Change is owned by byrna-pdp-warnings.js (#1364)
      show(root.querySelector('[data-state-cta]'), true);
      show(root.querySelector('[data-state-result]'), false);
      var sel = root.querySelector('[data-state-select]');
      if (sel) sel.focus();
      if (window.ByrnaGA4) {
        window.ByrnaGA4.featureClick('cart_pre_checkout_messaging', '1364', { surface: 'cart', element: 'state_change' });
      }
      return;
    }
    var confirmLink = e.target.closest('[data-confirm-state-link]');
    if (confirmLink) {
      var croot = confirmLink.closest('.theme-ajax-cart');
      var csel = croot && croot.querySelector('[data-state-select]');
      if (csel) { e.preventDefault(); csel.focus(); pulseAttention(csel); }
      if (window.ByrnaGA4) {
        window.ByrnaGA4.featureClick('cart_pre_checkout_messaging', '1364', { surface: 'cart', element: 'confirm_state_link' });
      }
      return;
    }
  });
})();
